import { css } from '@emotion/react'
import { ConsultaSeguimentoIndicatorBlock } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorBlock'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import {
  ExemplosVerticalField,
  ExemplosVerticalFieldOptions,
} from '@mchat-rf/consulta-seguimento/components/ExemplosVerticalField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { PassouFalhouEnum } from '@mchat-rf/consulta-seguimento/enums-consultaSeguimento'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { Heading, Text, VFlow } from 'bold-ui'
import { resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { Fragment, RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta11ExemplosEnum,
  ConsultaSeguimentoPergunta11Model,
} from './model-consultaSeguimentoPergunta11'
import { PERGUNTA_11_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta11'
import {
  calculateResultadoPergunta11,
  conditionToCalculatePergunta11,
  getPergunta11ResultadoText,
  hasPergunta11ExemplosFalhouAnySim,
  hasPergunta11ExemplosPassouAnySim,
  isPergunta11ExemplosAllFilled,
  isPergunta11ExemplosAllNao,
} from './utils-consultaSeguimentoPergunta11'

interface ConsultaSeguimentoPergunta11FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta11Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta11Field(props: Readonly<ConsultaSeguimentoPergunta11FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta11Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta11Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta11,
    calculateResultado: calculateResultadoPergunta11,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta11Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_11_SCROLL_CONFIG,
  })

  const isPerguntaExemplosAllNao = isPergunta11ExemplosAllNao(value?.PERGUNTA_11_EXEMPLOS)
  const isExemplosAllFilled = isPergunta11ExemplosAllFilled(value?.PERGUNTA_11_EXEMPLOS)
  const hasPassouAnySim = hasPergunta11ExemplosPassouAnySim(value?.PERGUNTA_11_EXEMPLOS)
  const hasFalhouAnySim = hasPergunta11ExemplosFalhouAnySim(value?.PERGUNTA_11_EXEMPLOS)
  const resultadoText = getPergunta11ResultadoText(value)
  const showPerguntaFrequencia = isExemplosAllFilled && hasPassouAnySim && hasFalhouAnySim

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_11}
            title={
              <ConsultaSeguimentoPerguntaHeaderTitle
                prefix='Quando você sorri para'
                sufix=', ele sorri de volta para você?'
              />
            }
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_11_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_11_HEADER')}
              />
            }
          />
        )}
        {value.PERGUNTA_11_HEADER === SimNaoEnum.NAO && (
          <VFlow id={getId('PERGUNTA_11_EXEMPLOS')}>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              <ConsultaSeguimentoIndicatorHelper
                title={
                  <Fragment>
                    <Text color='inherit' fontWeight='bold'>
                      O que faz{' '}
                    </Text>
                    <Text color='inherit' textDecoration='underline' fontWeight='normal'>
                      (perguntar usando o nome da criança)
                    </Text>
                    <Text color='inherit' fontWeight='bold'>
                      {' '}
                      sorrir?
                    </Text>
                  </Fragment>
                }
                subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
              />
            </VFlow>
            <VFlow vSpacing={0}>
              <ExemplosVerticalField
                name={name.PERGUNTA_11_EXEMPLOS}
                label='Responda todos os exemplos, conforme as respostas do genitor'
                titlePassou='Seu filho...'
                titleFalhou='Ele sempre...'
                options={PERGUNTA_11_EXEMPLOS_OPTIONS}
                required
                onChange={() => triggerScrollFor('PERGUNTA_11_EXEMPLOS')}
              />
              {isPerguntaExemplosAllNao && <ConsultaSeguimentoIndicatorBlock />}
              {showPerguntaFrequencia && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoIndicatorStep
                    text={
                      <VFlow vSpacing={0}>
                        <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                        <Heading level={3} color='inherit'>
                          Pergunte sobre a frequência
                        </Heading>
                      </VFlow>
                    }
                    style={styles.indicatorStep}
                  />
                  <div id={getId('PERGUNTA_11_FREQUENCIA')}>
                    <RadioGroupBoxField
                      name={name.PERGUNTA_11_FREQUENCIA}
                      label='Quais os que ele faz com mais frequência?'
                      options={[
                        {
                          label: 'Exemplos PASSOU',
                          value: PassouFalhouEnum.PASSOU,
                        },
                        {
                          label: 'Exemplos FALHOU',
                          value: PassouFalhouEnum.FALHOU,
                        },
                      ]}
                      required
                      onChange={() => triggerScrollFor('PERGUNTA_11_FREQUENCIA')}
                    />
                  </div>
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && <ConsultaSeguimentoIndicatorResultado resultado={value.resultado} text={resultadoText} />}
    </VFlow>
  )
}

const PERGUNTA_11_EXEMPLOS_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta11ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Sorri quando você sorri?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_QUANDO_VOCE_SORRI,
    },
    {
      label: 'Sorri quando você entra no quarto?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_QUANDO_VOCE_ENTRA_NO_QUARTO,
    },
    {
      label: 'Sorri quando você volta depois de ter estado fora de casa?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_QUANDO_VOCE_VOLTA,
    },
  ],
  FALHOU: [
    {
      label: 'Está sempre sorrindo?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.ESTA_SEMPRE_SORRINDO,
    },
    {
      label: 'Sorri para um brinquedo ou atividade favorita?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_PARA_BRINQUEDO_OU_ATIVIDADE,
    },
    {
      label: 'Sorri ao acaso ou sem nenhum motivo especial?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_AO_ACASO_OU_SEM_MOTIVO,
    },
  ],
}

const styles = {
  indicatorStep: css`
    width: auto;
  `,
}
